import React, { useState, useEffect } from 'react'
import { imagesApi, CollectionImage } from '../../services/api'

interface ImageManagerProps {
  collection: 'boys' | 'girls'
  onClose: () => void
}

export const ImageManager: React.FC<ImageManagerProps> = ({ collection, onClose }) => {
  const [images, setImages] = useState<CollectionImage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isUploading, setIsUploading] = useState(false)
  const [isSavingMetadata, setIsSavingMetadata] = useState(false)
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadAltText, setUploadAltText] = useState('')
  const [uploadOrderIndex, setUploadOrderIndex] = useState(1)
  const [editingImage, setEditingImage] = useState<CollectionImage | null>(null)
  const [showEditForm, setShowEditForm] = useState(false)
  const [editAltText, setEditAltText] = useState('')
  const [editOrderIndex, setEditOrderIndex] = useState(1)
  const [error, setError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const collectionName = collection === 'boys' ? 'Niños' : 'Niñas'

  useEffect(() => {
    loadImages()
  }, [collection])

  const loadImages = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await imagesApi.getByCollection(collection)
      if (response.success && response.data) {
        setImages(response.data)
      }
    } catch (err) {
      console.error('Error al cargar imágenes:', err)
      setError('Error al cargar las imágenes')
    } finally {
      setIsLoading(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validar tipo de archivo
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
      if (!allowedTypes.includes(file.type)) {
        setError('Tipo de archivo no permitido. Solo se permiten imágenes (JPEG, PNG, WebP, GIF).')
        return
      }

      // Validar tamaño (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('El archivo es demasiado grande. Máximo 5MB.')
        return
      }

      setUploadFile(file)
      setError(null)
    }
  }

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!uploadFile) {
      setError('Selecciona un archivo')
      return
    }

    // Validar orden duplicado
    const isDuplicateOrder = images.some(img => img.order_index === uploadOrderIndex)
    if (isDuplicateOrder) {
      alert(`El número de orden ${uploadOrderIndex} ya está en uso. Por favor elige otro.`)
      return
    }

    try {
      setIsUploading(true)
      setError(null)

      const response = await imagesApi.upload(
        uploadFile,
        collection,
        uploadAltText || `Imagen de ${collectionName}`,
        uploadOrderIndex
      )

      if (response.success) {
        await loadImages() // Recargar la lista
        setShowUploadForm(false)
        setUploadFile(null)
        setUploadAltText('')
        setUploadOrderIndex(1)
      }
    } catch (err) {
      console.error('Error al subir imagen:', err)
      setError('Error al subir la imagen')
    } finally {
      setIsUploading(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta imagen?')) {
      return
    }

    try {
      setError(null)
      const response = await imagesApi.delete(id)

      if (response.success) {
        await loadImages() // Recargar la lista
      }
    } catch (err) {
      console.error('Error al eliminar imagen:', err)
      setError('Error al eliminar la imagen')
    }
  }

  const handleToggleActive = async (id: number, isActive: boolean) => {
    try {
      setError(null)
      const response = await imagesApi.update(id, { is_active: !isActive })

      if (response.success) {
        await loadImages() // Recargar la lista
      }
    } catch (err) {
      console.error('Error al actualizar imagen:', err)
      setError('Error al actualizar la imagen')
    }
  }

  const handleEditClick = (image: CollectionImage) => {
    setEditingImage(image)
    setEditAltText(image.alt_text)
    setEditOrderIndex(image.order_index)
    setFormError(null)
    setShowEditForm(true)
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!editingImage) return

    // Validar orden duplicado (excluyendo la imagen actual)
    const isDuplicateOrder = images.some(img =>
      img.id !== editingImage.id && img.order_index === editOrderIndex
    )

    if (isDuplicateOrder) {
      setFormError(`El número de orden ${editOrderIndex} ya está en uso por otra imagen. Por favor elige otro.`)
      return
    }

    setFormError(null)

    try {
      setIsSavingMetadata(true)
      setError(null)

      const response = await imagesApi.update(editingImage.id, {
        alt_text: editAltText,
        order_index: editOrderIndex
      })

      if (response.success) {
        await loadImages()
        setShowEditForm(false)
        setEditingImage(null)
      }
    } catch (err) {
      console.error('Error al actualizar imagen:', err)
      setError('Error al actualizar la imagen')
    } finally {
      setIsSavingMetadata(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Gestión de Imágenes - Colección {collectionName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Botón para subir nueva imagen */}
        <div className="mb-6">
          <button
            onClick={() => setShowUploadForm(!showUploadForm)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <span>+</span>
            Subir Nueva Imagen
          </button>
        </div>

        {/* Formulario de subida */}
        {showUploadForm && (
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="text-lg font-semibold mb-4">Subir Nueva Imagen</h3>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Archivo de Imagen
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  required
                />
                <p className="text-xs text-gray-500 mt-2">
                  Resolución recomendada: 900×1200px (proporción 3:4 vertical). Máx 5MB.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Texto Alternativo
                </label>
                <input
                  type="text"
                  value={uploadAltText}
                  onChange={(e) => setUploadAltText(e.target.value)}
                  placeholder={`Imagen de ${collectionName}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orden
                </label>
                <input
                  type="number"
                  value={uploadOrderIndex}
                  onChange={(e) => setUploadOrderIndex(parseInt(e.target.value) || 1)}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={isUploading || !uploadFile}
                  className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg"
                >
                  {isUploading ? 'Subiendo...' : 'Subir Imagen'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowUploadForm(false)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Formulario de edición */}
        {showEditForm && editingImage && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Editar Imagen</h3>
              {formError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
                  {formError}
                </div>
              )}
              <form onSubmit={handleUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Texto Alternativo
                  </label>
                  <input
                    type="text"
                    value={editAltText}
                    onChange={(e) => setEditAltText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Orden
                  </label>
                  <input
                    type="number"
                    value={editOrderIndex}
                    onChange={(e) => setEditOrderIndex(parseInt(e.target.value) || 1)}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Menor número aparece primero. Si repites números, el orden secundario es por fecha.
                  </p>
                </div>

                <div className="flex gap-2 justify-end mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditForm(false)
                      setEditingImage(null)
                    }}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSavingMetadata}
                    className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg"
                  >
                    {isSavingMetadata ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Lista de imágenes */}
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-2 text-gray-600">Cargando imágenes...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {images.map((image) => {
              return (
                <div key={image.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="overflow-hidden mb-3">
                    <img
                      src={image.full_image_url || image.image_url}
                      alt={image.alt_text}
                      className="w-full aspect-[3/4] object-cover rounded-lg"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.src = 'https://via.placeholder.com/300x400/CCCCCC/666666?text=Imagen+no+disponible'
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 truncate" title={image.alt_text}>
                      {image.alt_text}
                    </p>
                    <p className="text-xs text-gray-500">
                      Orden: {image.order_index}
                    </p>

                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${image.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                        }`}>
                        {image.is_active ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>

                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleToggleActive(image.id, image.is_active)}
                        className={`px-3 py-1 rounded text-xs ${image.is_active
                          ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                          : 'bg-green-500 hover:bg-green-600 text-white'
                          }`}
                      >
                        {image.is_active ? 'Desactivar' : 'Activar'}
                      </button>
                      <button
                        onClick={() => handleDelete(image.id)}
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs"
                      >
                        Eliminar
                      </button>
                      <button
                        onClick={() => handleEditClick(image)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs"
                      >
                        Editar Orden
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {!isLoading && images.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No hay imágenes en esta colección.</p>
            <p className="text-sm">Sube la primera imagen usando el botón de arriba.</p>
          </div>
        )}
      </div>
    </div>
  )
}
